// Taking sets out of the history — one set, a session, a day — reversibly.
//
// Shared by Timeline and Film so the two history screens remove the same way:
// the sets leave the screen at once, the undo bar says what went, and the
// records are only deleted when the undo window shuts (core/undo.js). So an
// Undo puts back the SAME rows with the same ids, which re-adding them would
// not — and nothing is asked first, because DESIGN.md: "an action that can be
// undone should be undone through the undo bar".
//
// A filmed set loses its POINTER only. The clip is in the phone's library and
// this app never had a copy of it (lib/liftvideo.js), so nothing here touches
// Photos. The screens say so.
//
// The Ramp reads attempts to find the attempt for a set by weight (to attach a
// clip to it). Its "which set am I on" is session storage, not attempts, so a
// removal never moves the lifter's place in a ramp; a removed set is simply
// recorded afresh the next time Next set or Film passes it.

import { attempts, removeAttempt } from '../store.js';
import { remove as removeWithUndo } from '../core/undo.js';
import { haptic } from '../core/util.js';

/**
 * Hide these attempts now; delete them when the undo window closes.
 *
 * @param {object[]} rows  the attempts to remove
 * @param {string}   text  what the undo bar says went ("Session removed · 7 sets.")
 * @returns {number} how many were removed
 */
export function forgetSets(rows, text) {
  const ids = new Set(rows.map((r) => String(r.id)));
  const before = attempts.value;
  const gone = [];
  before.forEach((a, i) => { if (ids.has(String(a.id))) gone.push({ a, i }); });
  if (!gone.length) return 0;

  attempts.value = before.filter((a) => !ids.has(String(a.id)));
  haptic('pop');
  removeWithUndo({
    text,
    commit: () => { for (const { a } of gone) removeAttempt(a.id); },
    revert: () => {
      // Back where they were, so anything that reads the list in order (the
      // Ramp's first match by weight) finds what it found before.
      const next = attempts.value.filter((a) => !ids.has(String(a.id)));
      for (const { a, i } of gone) next.splice(Math.min(i, next.length), 0, a);
      attempts.value = next;
    },
  });
  return gone.length;
}

/** "1 set", "7 sets". */
export const setCount = (n) => `${n} ${n === 1 ? 'set' : 'sets'}`;
